// @flow
import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import { reduxForm, SubmissionError, Field as RFField, change, type FormProps, type FieldProps } from "redux-form";
import { Segment, Button, Message, Form, Header, Responsive, Image, Transition, Icon } from "semantic-ui-react";
import { connect, useDispatch, useSelector } from "react-redux";
import classnames from "classnames";

import { loadCustomTrackingData } from "Commons/Utilities/Tracking/TrackingHandler";
import { Field } from "../../Components";

import "./PromotionCode.scss";
import { checkPromotionCode, fetchPromotionCode, resetPromotionCode } from "redux/actions/couponCodes";
import { TextSegment } from "./CouponCodeBox";
import { isPresent } from "Commons/Utilities";
import type { CouponCodeT } from "./misc/Types";

type InputProps = {|
  t: typeof TFunction,
  handleSubmit: $PropertyType<FormProps, "handleSubmit">,
  submitting: $PropertyType<FormProps, "submitting">,
|} & FieldProps;

const PromotionInput = (props: InputProps) => {
  const { t, handleSubmit, submitting } = props;

  return (
    <Form.Input
      {...props.input}
      type="text"
      action={
        <Button primary disabled={submitting} loading={submitting} onClick={handleSubmit}>
          {t("wizard.overview.promotion.button")}
        </Button>
      }
    />
  );
};

const Title = ({ title, subTitle }: { title: string, subTitle: string }) => (
  <div className="PromotionCode title">
    <Header>
      {title}
      <Header.Subheader>{subTitle}</Header.Subheader>
    </Header>
  </div>
);

type Props = {|
  t: typeof TFunction,
  promotionCode: CouponCodeT,
  fetchingError: ?string,
|} & FormProps;

let PromotionCodeChecker = (props: Props) => {
  const {
    handleSubmit,
    t,
    error,
    submitting,
    mobileChangeService,
    className,
    width = 5,
    labelWidth,
    noDivider,
  } = props;
  const [showCode, setShowCode]: [boolean, Function] = useState(false);
  const [showSuccess, setShowSuccess]: [boolean, Function] = useState(true);

  const dispatch = useDispatch();

  const internalSubmit = (params) => {
    setShowCode(false);
    setShowSuccess(true);

    handleSubmit(params);
  };

  const promotionCodeCampaignId = loadCustomTrackingData("ppc_campaign_id");

  const BannerImage = () => (
    <Segment raised className={"coupon-code-box-wrapper"}>
      <div className={"coupon-code-box-lower-wrapper"}>
        <Responsive minWidth={Responsive.onlyTablet.minWidth}>
          <Image fluid src={couponCode.imageUrl} />
          <div className={"wizard-funnel-promocode"}>
            <TextSegment couponCodeTitle={couponCode.title} couponCodeDescription={couponCode.description} />
          </div>
        </Responsive>
        <Responsive {...Responsive.onlyMobile}>
          <Image fluid src={couponCode.imageMobileUrl} className="mt1" />
          <div className={"wizard-funnel-promocode"}>
            <TextSegment couponCodeTitle={couponCode.title} couponCodeDescription={couponCode.description} />
          </div>
        </Responsive>
      </div>
    </Segment>
  );

  useEffect(() => {
    dispatch(fetchPromotionCode(promotionCodeCampaignId));

    return () => {
      dispatch(resetPromotionCode());
    };
  }, [dispatch, promotionCodeCampaignId]);

  const { couponCode, promotionCode, fetchingError } = useSelector(
    ({ couponCodes: { couponCode, promotionCode, fetchingError } }) => ({
      couponCode,
      promotionCode,
      fetchingError,
    })
  );

  useEffect(() => {
    if (couponCode && couponCode.disableCSCodeInputField) {
      dispatch(change("ChangeService", "promotionCodeCampaignId", couponCode.promotionCodeCampaignId));
    }
  }, [dispatch, couponCode, props]);

  if (!couponCode) {
    return null;
  }

  if (couponCode && couponCode.disableCSCodeInputField) {
    return (
      <>
        <Title
          title={
            couponCode.disableCSCodeInputField
              ? t("wizard.overview.promotion.title.banner")
              : t("wizard.overview.promotion.title.form")
          }
          subTitle={
            couponCode.disableCSCodeInputField
              ? t("wizard.overview.promotion.subTitle.banner")
              : t("wizard.overview.promotion.subTitle.form")
          }
        />
        <Segment>
          <BannerImage />
        </Segment>
      </>
    );
  }

  return (
    <>
      {!mobileChangeService && (
        <Title
          title={
            couponCode.disableCSCodeInputField
              ? t("wizard.overview.promotion.title.banner")
              : t("wizard.overview.promotion.title.form")
          }
          subTitle={
            couponCode.disableCSCodeInputField
              ? t("wizard.overview.promotion.subTitle.banner")
              : t("wizard.overview.promotion.subTitle.form")
          }
        />
      )}
      <Segment
        basic={mobileChangeService}
        className={classnames(mobileChangeService && "PromotionCode mobileChangeService", className)}
      >
        <Field.Component labelWidth={labelWidth} noDivider={noDivider} label={t("wizard.overview.promotion.label")}>
          <Form.Field width={width}>
            <RFField
              name="promotioncode"
              component={PromotionInput}
              props={{ handleSubmit: internalSubmit, submitting, t }}
            />
          </Form.Field>
        </Field.Component>
        {promotionCode && (
          <Segment raised className={"coupon-code-box-wrapper"}>
            <>
              <Transition
                transitionOnMount
                visible={showSuccess}
                duration={1000}
                onShow={() => setTimeout(() => setShowSuccess(false), 1000)}
                onHide={() => setShowCode(true)}
              >
                <div>
                  <div className="promotion-code code-checkbox">
                    <Icon color="green" name="check" size="huge" />
                    <div>Code OK!</div>
                  </div>
                </div>
              </Transition>
              <Transition visible={showCode} duration={1000} transitionOnMount>
                <div className={"coupon-code-box-lower-wrapper"}>
                  <Responsive minWidth={Responsive.onlyTablet.minWidth}>
                    <Image fluid src={promotionCode.imageUrl} />
                    <div className={"wizard-funnel-promocode"}>
                      <TextSegment
                        couponCodeTitle={promotionCode.title}
                        couponCodeDescription={promotionCode.description}
                      />
                    </div>
                  </Responsive>
                  <Responsive {...Responsive.onlyMobile}>
                    <Image fluid src={promotionCode.imageMobileUrl} className="mt1" />
                    <div className={"wizard-funnel-promocode"}>
                      <TextSegment
                        couponCodeTitle={promotionCode.title}
                        couponCodeDescription={promotionCode.description}
                      />
                    </div>
                  </Responsive>
                </div>
              </Transition>
            </>
          </Segment>
        )}
        {fetchingError || promotionCode === "" ? (
          <Message negative>{t("wizard.overview.promotion.error")}</Message>
        ) : (
          error && <Message negative>{t("wizard.overview.promotion.validationError")}</Message>
        )}
      </Segment>
    </>
  );
};

const mapDispatchToProps = {
  checkPromotionCode,
  resetPromotionCode,
};

PromotionCodeChecker = withTranslation("changeService")(PromotionCodeChecker);
PromotionCodeChecker = reduxForm({
  form: "promotionCode",
  onSubmit: (values, dispatch, props) => {
    if (!isPresent(values) || !isPresent(values.promotioncode)) {
      props.resetPromotionCode();
      throw new SubmissionError({
        _error: true,
      });
    } else {
      return new Promise((resolve, reject) => {
        return props.checkPromotionCode(values, resolve, reject);
      });
    }
  },
  onSubmitSuccess: (result, dispatch, props) => {
    dispatch(change("ChangeService", "promotionCodeCampaignId", result.promotionCodeCampaignId));
  },
  onSubmitFail: (errors, dispatch, error, props) => {
    dispatch(change("ChangeService", "promotionCodeCampaignId", null));
  },
})(PromotionCodeChecker);

PromotionCodeChecker = connect(null, mapDispatchToProps)(PromotionCodeChecker);

export default PromotionCodeChecker;
